import React from 'react';
import { getClaimRequirements } from '../data/claimRequirements';

// Renders the governing agreement's ELIGIBILITY (valid entry) and PAYABLE (content)
// requirements before the "What Happened" card. Auto-resolves off the claim's agreement;
// renders NOTHING when the agreement's claims rule is not catalogued ("if nothing, then nothing").
// Everything here is information — it never blocks filing.

// A VALUE that is an array - matchedRules, the selected rule basis - is filled when it has entries.
// String([]) is '' and String([{...}]) is '[object Object]', so the old test happened to give the
// right answer for the wrong reason; stated explicitly here so it stays right on purpose.
const hasValue = (v) => Array.isArray(v) ? v.length > 0 : String(v ?? '').trim() !== '';

const filled = (form, f, dl) => {
  if (f === '__inherent__') return true;
  // The deadline row reflects the real computed window. Late is shown as unmet -- honestly --
  // and filing is still never blocked: the banner above states the member may file anyway and
  // that timeliness is the LC/GC's call, not the software's.
  if (f === '__deadline__') return dl ? dl.remaining >= 0 : false;
  // `f` as an array means EVERY ONE of these keys must be present.
  if (Array.isArray(f)) return f.every(k => hasValue(form?.[k]));
  return hasValue(form?.[f]);
};

function computeDeadline(incidentDate, days) {
  if (!incidentDate || !days) return null;
  const d = new Date(incidentDate + 'T00:00:00');
  if (isNaN(d.getTime())) return null;
  d.setDate(d.getDate() + days - 1);   // ruling 314: the occurrence is day one
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const remaining = Math.round((d.getTime() - today.getTime()) / 86400000);
  return { date: d.toISOString().slice(0, 10), remaining };
}

const C = {
  card: '#0D1B2A', line: '#8AA4BF', head: '#E8A020', ok: '#40C070',
  pend: '#C08A2E', border: '#2E6B4A', text: '#D8E2EE',
};

function Row({ ok, children, hint }) {
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', padding: '3px 0', fontSize: 12.5, color: C.text }}>
      <span style={{ color: ok ? C.ok : C.pend, fontWeight: 700, minWidth: 12 }}>{ok ? '✓' : '○'}</span>
      <span>{children}{hint && <span style={{ color: C.line, fontSize: 11 }}> — {hint}</span>}</span>
    </div>
  );
}

// The place on the page where an open item is answered (Bob 2026-10-17: "where to look, what to read next and do next,
// and sometimes where as well"). A caller passes `where` = { field: 'place' }; an unticked row then says where.
const whereOf = (where, f) => {
  if (!where || !f) return '';
  const keys = Array.isArray(f) ? f : [f];
  const hit = keys.map(k => where[k]).find(Boolean);
  return hit || '';
};

// `omit` (ruling 261): payable-content fields a caller does not list. Absent, nothing is omitted.
// `part` (Bob 2026-10-17, the page-order fix): 'deadline' draws only the filing-deadline line, for the top of the page;
// 'checklist' draws only the requirements, for just above Continue. Absent: the whole panel, as before (NewClaim).
export default function EligibilityPayablePanel({ agreement, form, omit, part, where }) {
  const req = getClaimRequirements(agreement);
  if (!req) return null;

  const dl = computeDeadline(form?.incidentDate, req.filingWindowDays);

  if (part === 'deadline') {
    if (!dl) return null;
    return (
      <div style={{
        margin: '0 0 12px', padding: '8px 12px', borderRadius: 6,
        background: dl.remaining < 0 ? '#2D0A0A' : '#0A1A0A',
        border: `1px solid ${dl.remaining < 0 ? '#C0392B' : C.border}`,
        fontSize: 12.5, color: dl.remaining < 0 ? '#FF8A8A' : C.ok, fontWeight: 600,
      }}>
        Filing deadline: <strong>{dl.date}</strong> ({req.filingWindowDays} days from the date of occurrence, {req.claimsRule})
        {dl.remaining >= 0 ? ` — ${dl.remaining} day${dl.remaining === 1 ? '' : 's'} remaining` : ` — ${Math.abs(dl.remaining)} day${Math.abs(dl.remaining) === 1 ? '' : 's'} past. You may still file; timeliness is your Local and General Chairperson's call.`}
      </div>
    );
  }

  const hintFor = (field, note) => {
    if (filled(form, field, dl)) return note;
    const w = whereOf(where, field);
    return w ? (note ? note + ' Answered at: ' + w + '.' : 'Answered at: ' + w + '.') : note;
  };

  return (
    <div className="card" style={{ background: C.card, borderLeft: `4px solid ${C.border}`, marginBottom: 14 }}>
      <div className="card-header">
        <div>
          <div className="card-title">{part === 'checklist' ? 'What this claim still needs' : 'Before you file — what this claim requires'}</div>
          <div className="card-subtitle" style={{ color: C.head, fontSize: 11, fontWeight: 600 }}>
            {req.claimsRule} · {agreement}
          </div>
        </div>
      </div>

      {dl && part !== 'checklist' && (
        <div style={{
          margin: '8px 0 12px', padding: '8px 12px', borderRadius: 6,
          background: dl.remaining < 0 ? '#2D0A0A' : '#0A1A0A',
          border: `1px solid ${dl.remaining < 0 ? '#C0392B' : C.border}`,
          fontSize: 12.5, color: dl.remaining < 0 ? '#FF8A8A' : C.ok, fontWeight: 600,
        }}>
          Filing deadline: <strong>{dl.date}</strong> ({req.filingWindowDays} days from the date of occurrence)
          {dl.remaining >= 0 ? ` — ${dl.remaining} day${dl.remaining === 1 ? '' : 's'} remaining` : ` — ${Math.abs(dl.remaining)} day${Math.abs(dl.remaining) === 1 ? '' : 's'} past. You may still file; timeliness is the LC/GC's call, not the software's.`}
        </div>
      )}

      <div style={{ fontSize: 11, color: C.head, fontWeight: 700, letterSpacing: 0.4, margin: '4px 0 2px' }}>
        TO BE A VALID CLAIM (ELIGIBILITY)
      </div>
      {req.eligibility.map(e => (
        <Row key={e.id} ok={filled(form, e.field, dl)} hint={hintFor(e.field, e.note)}>{e.label}</Row>
      ))}

      <div style={{ fontSize: 11, color: C.head, fontWeight: 700, letterSpacing: 0.4, margin: '10px 0 2px' }}>
        WHAT A PAYABLE CLAIM MUST CONTAIN
      </div>
      {req.payable.filter(p => !(Array.isArray(omit) && typeof p.field === 'string' && omit.includes(p.field)))
        .map(p => (Array.isArray(omit) && Array.isArray(p.field) && p.field.some(k => omit.includes(k))
          ? { ...p, field: p.field.filter(k => !omit.includes(k)), label: p.label.replace(/, tour\)/, ')') }
          : p)).map(p => (
        <Row key={p.id} ok={filled(form, p.field)} hint={hintFor(p.field, p.suggest ? 'auto-drafted suggestion' : undefined)}>{p.label}</Row>
      ))}

      <div style={{ marginTop: 10, fontSize: 10.5, color: C.line, fontStyle: 'italic' }}>
        Source: {req.source}. These requirements are information — you can always file; nothing here blocks a claim.
      </div>
    </div>
  );
}
